/** Local calendar-date helpers (ISO yyyy-mm-dd strings, no time zones). */

const pad = (n: number) => String(n).padStart(2, "0");

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "2026-10-19" → local Date at midnight; null when not a valid ISO date. */
export function parseIsoDate(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** First 10 chars of a date/datetime string, or null. */
export function dateOnly(value: unknown): string | null {
  const d = parseIsoDate(value);
  return d ? isoDate(d) : null;
}

export function addDays(iso: string, days: number): string {
  const d = parseIsoDate(iso) ?? new Date();
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

export function todayIso(now: Date = new Date()): string {
  return isoDate(now);
}

/** Monday of the week containing `iso`. */
export function weekStart(iso: string): string {
  const d = parseIsoDate(iso) ?? new Date();
  const offset = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  d.setDate(d.getDate() - offset);
  return isoDate(d);
}

export function weekDays(start: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** "2026-10" for the month containing `iso`. */
export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

export function isValidMonthKey(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

export function monthBounds(key: string): { start: string; end: string; days: string[] } {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const start = isoDate(first);
  const days = Array.from({ length: last.getDate() }, (_, i) => isoDate(new Date(y, m - 1, i + 1)));
  return { start, end: isoDate(last), days };
}

export function shiftMonth(key: string, delta: number): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

export function isWeekend(iso: string): boolean {
  const day = parseIsoDate(iso)?.getDay();
  return day === 0 || day === 6;
}

const shortFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
const longFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const monthFmt = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });
const weekdayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "short" });

export function formatShort(iso: string | null | undefined): string {
  const d = parseIsoDate(iso);
  return d ? shortFmt.format(d) : "";
}

export function formatLong(iso: string | null | undefined): string {
  const d = parseIsoDate(iso);
  return d ? longFmt.format(d) : "";
}

export function formatMonth(key: string): string {
  return monthFmt.format(parseIsoDate(`${key}-01`) ?? new Date());
}

export function formatWeekday(iso: string): string {
  const d = parseIsoDate(iso);
  return d ? weekdayFmt.format(d) : "";
}

/** "13 – 19 Oct 2026" */
export function formatRange(start: string, end: string): string {
  const s = parseIsoDate(start);
  const e = parseIsoDate(end);
  if (!s || !e) return "";
  if (s.getFullYear() !== e.getFullYear()) return `${longFmt.format(s)} – ${longFmt.format(e)}`;
  if (s.getMonth() === e.getMonth()) return `${s.getDate()} – ${longFmt.format(e)}`;
  return `${shortFmt.format(s)} – ${longFmt.format(e)}`;
}

export function toNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export function formatHoursShort(n: number): string {
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(n);
}
